import { Celestial } from '@otas/utils/custom-components/panchang-calendar/kollavarsham/celestial';
import { Calendar } from '@otas/utils/custom-components/panchang-calendar/kollavarsham/calendar';
import { SakaDate } from '@otas/utils/custom-components/panchang-calendar/kollavarsham/saka-date';
import { KollavarshamDate } from '@otas/utils/custom-components/panchang-calendar/kollavarsham/kolla-varsham-date';
import { JulianDate } from '@otas/utils/custom-components/panchang-calendar/kollavarsham/julian-date';
import { MathHelper } from '@otas/utils/custom-components/panchang-calendar/kollavarsham/math-helper';
import { mldates20, mldates19 } from './kolla-varsham-const-dates';
import * as _ from 'underscore';
import moment from 'moment';
import { IST_OFFSET_MS, istDiffDays, istEpoch, istParts } from '../ist';
import { KollaVarshamHelper } from './kolla-varsham-helper';
import { ML_MONTHS, WEEKDAYS } from 'app/app.constants';
const Trivandrum = {
    latitude: 8.520929,
    longitude: 76.941200
};
export class Calculations {
    longitude;
    latitude;
    celestial;
    calendar;
    constructor(settings) {
        this.longitude = settings.longitude;
        this.latitude = settings.latitude;
        this.celestial = new Celestial(settings.system);
        this.calendar = new Calendar(this.celestial);
    }

    static getPaksa(tithi) {
        return tithi >= 15 ? 'Krsnapaksa' : 'Suklapaksa';
    }

    fromGregorianToSaka(gregorianDate) {
        const year = gregorianDate.getFullYear();

        const julianDay = Calendar.gregorianDateToJulianDay(gregorianDate);
        const ahargana = Calendar.julianDayToAhargana(julianDay);

        const timeAsFractionalDayAt6AM = 0.25; // time at 6 o'clock
        const sakaDate = this.celestialCalculations(ahargana, year, julianDay, timeAsFractionalDayAt6AM);

        const timeFromGregorianDate = Calendar.timeIntoFractionalDay(gregorianDate);
        const sakaDateAtGivenTime = this.celestialCalculations(ahargana, year, julianDay, timeFromGregorianDate);
        sakaDate.naksatra = sakaDateAtGivenTime.naksatra;

        sakaDate.gregorianDate = gregorianDate; // set the input gregorian date
        return sakaDate;
    }

    celestialCalculations(ahargana, year, julianDay, timeAsFractionalDay) {
        ahargana += timeAsFractionalDay;

        // Definition of desantara
        const desantara = (this.longitude - Trivandrum.longitude) / 360;

        // desantara
        ahargana -= desantara;

        // time of sunrise at local latitude
        const equationOfTime = this.celestial.getDaylightEquation(year, this.latitude, ahargana);
        ahargana -= equationOfTime;
        const { sunriseHour, sunriseMinute } = Celestial.getSunriseTime(timeAsFractionalDay, equationOfTime);

        const { trueSolarLongitude, trueLunarLongitude } = this.celestial.setPlanetaryPositions(ahargana);

        // finding tithi and longitude of conjunction
        const tithi = Celestial.getTithi(trueSolarLongitude, trueLunarLongitude);

        // last conjunction & next conjunction
        const lastConjunctionLongitude = this.celestial.getLastConjunctionLongitude(ahargana, tithi);
        const nextConjunctionLongitude = this.celestial.getNextConjunctionLongitude(ahargana, tithi);

        const masaNum = Calendar.getMasaNum(trueSolarLongitude, lastConjunctionLongitude);

        // kali and Saka era
        const kaliYear = this.calendar.aharganaToKali(ahargana + (4 - masaNum) * 30);
        const sakaYear = Calendar.kaliToSaka(kaliYear);

        let tithiDay = MathHelper.truncate(tithi) + 1;

        const paksa = Calculations.getPaksa(tithiDay);
        tithiDay = tithiDay >= 15 ? tithiDay -= 15 : tithiDay;

        const { sauraMasa, sauraDivasa } = this.calendar.getSauraMasaAndSauraDivasa(ahargana, desantara);

        const sakaDate = new SakaDate(sakaYear, masaNum, tithiDay, paksa);

        sakaDate.julianDay = MathHelper.truncate(julianDay + 0.5);
        sakaDate.ahargana = MathHelper.truncate(ahargana + 0.5); // Remove the decimals
        sakaDate.originalAhargana = ahargana;
        sakaDate.sauraMasa = sauraMasa;
        sakaDate.sauraDivasa = sauraDivasa;
        sakaDate.naksatra = Calendar.getNaksatra(trueLunarLongitude);
        sakaDate.kaliYear = kaliYear;
        sakaDate.adhimasa = Calendar.getAdhimasa(lastConjunctionLongitude, nextConjunctionLongitude);
        sakaDate.fractionalTithi = MathHelper.fractional(tithi);
        sakaDate.sunriseHour = sunriseHour;
        sakaDate.sunriseMinute = sunriseMinute;
        return sakaDate;
    }

    fromGregorian(gregorianDate) {

        const sakaDate = this.fromGregorianToSaka(gregorianDate);

        return sakaDate.generateKollavarshamDate();

    }

    toGregorian(kollavarshamDate) {

        throw new Error('Not implemented');
    }

    toGregorianFromSaka(sakaDate) {

        const sakaYear = sakaDate.year;
        const masaNum = sakaDate.month;
        let tithiDay = sakaDate.tithi;
        const paksa = sakaDate.paksa;

        if (paksa === 'Krsnapaksa') {
            tithiDay += 15;
        }
        const kaliYear = Calendar.sakaToKali(sakaYear);
        const ahargana = this.calendar.kaliToAhargana(kaliYear, masaNum, tithiDay);

        let julianDay = Calendar.aharganaToJulianDay(ahargana);
        julianDay += 0.5;

        const modernDate = Calendar.julianDayToModernDate(julianDay);
        if (JulianDate.prototype.isPrototypeOf(modernDate)) {
        }

        // TODO: Not happy that the empty constructor will make this with MalayalamYear => 1, MalayalamMonth => 1, and MalayalamDate => 1
        // TODO: Think this through before implementing toGregorian above
        const kollavarshamDate = new KollavarshamDate();
        kollavarshamDate.gregorianDate = modernDate;
        kollavarshamDate.julianDay = julianDay;
        kollavarshamDate.ahargana = ahargana;
        kollavarshamDate.sakaDate = sakaDate;

        return kollavarshamDate;
    }
    customCalculation(date: Date | any) {
        // IST-pinned: the Malayalam calendar day is Kerala's day, so decompose
        // the instant on the IST wall clock (a browser west of IST used to get
        // the previous day's mlDay/masa/weekday here).
        const ist = istParts(date instanceof Date ? date : new Date(date * 1));
        const year = ist.year;
        const month = ist.month + 1;
        const currentDate = ist.day;
        // if (year >= 2000) {
        let firstMlDaPrevMonth;
        let firstMlDay;
        if (year === 2000) {
            firstMlDay = this.getMlDate20(year, month, currentDate);
            if (month === 1) {
                firstMlDaPrevMonth = this.getMlDate19(year - 1, 12, currentDate);

            } else {
                firstMlDaPrevMonth = this.getMlDate20(year, month - 1, currentDate);

            }
        } else if (year > 2000) {
            firstMlDaPrevMonth = this.getMlDate20(month !== 1 ? year : year - 1, month !== 1 ? month - 1 : 12, currentDate);
            firstMlDay = this.getMlDate20(year, month, currentDate);
        } else {
            firstMlDaPrevMonth = this.getMlDate19(month !== 1 ? year : year - 1, month !== 1 ? month - 1 : 12, currentDate);
            firstMlDay = this.getMlDate19(year, month, currentDate);
        }

        // console.log(firstMlDaPrevMonth, 'firstMlDaPrevMonth');
        let mlday;
        let mlMonth;
        let mlYear = firstMlDay.mlYear;
        let mlMonthNumber = firstMlDay.mlMonth;
        if (currentDate < firstMlDay.enDate) {
            // Day count from the ML-month first day, on the IST day grid.
            const diffDays = istDiffDays(date,
                istEpoch(firstMlDaPrevMonth.enYear, firstMlDaPrevMonth.enMonth - 1, firstMlDaPrevMonth.enDate));

            mlday = 1 + diffDays;
            mlMonthNumber = firstMlDaPrevMonth.mlMonth
            mlYear = firstMlDaPrevMonth.mlYear
        } else if (currentDate === firstMlDay.enDate) {
            mlday = 1;

        } else {
            const diffDays = istDiffDays(date,
                istEpoch(firstMlDay.enYear, firstMlDay.enMonth - 1, firstMlDay.enDate));
            mlday = 1 + diffDays;
        }
        mlMonth = ML_MONTHS[mlMonthNumber - 1];

        // console.log(mlday, 'firstMlDay');
        const weekday = ist.weekday;
        return {
            date: mlday, mlMasaName: mlMonth.ml,
            masaName: mlMonth.en, month: mlMonthNumber, year: mlYear, weekday,
            weekdayName: WEEKDAYS[weekday].en, mlWeekdayName: WEEKDAYS[weekday].ml,
            // UTC-mode moment shifted by the IST offset = IST wall clock.
            week: this.weekOfMonth(moment.utc(date * 1 + IST_OFFSET_MS))
        };
        // } else {

        // }

    }
    weekOfMonth(date: moment.Moment) {
        let weekInYearIndex = date.week();
        if (date.year() !== date.weekYear()) {
            weekInYearIndex = date.clone().subtract(1, 'week').week() + 1;
        }
        const weekIndex = weekInYearIndex - moment(date).startOf('month').week() + 1;        
        return weekIndex;
    }
    getMlDate20(year: number, month: number, day: number): KollaVarshamHelper {
        const mlDate20 = mldates20;
        const firstMlDay = _.find(mlDate20, (md: KollaVarshamHelper) => md.enYear === year && md.enMonth === month);
        if (firstMlDay) {
            return firstMlDay;
        }
        return undefined;
    }
    getMlDate19(year: number, month: number, day: number): KollaVarshamHelper {
        const mlDate19 = mldates19;
        const firstMlDay = _.find(mlDate19, (md: KollaVarshamHelper) => md.enYear === year && md.enMonth === month);
        if (firstMlDay) {
            return firstMlDay;
        }
        return undefined;
    }

}
